import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import {
  Box,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import React from "react";

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Parses "4Q2025" → { q: 4, year: 2025 } */
export function parseQuarter(s: string): { q: number; year: number } {
  return { q: parseInt(s[0], 10), year: parseInt(s.slice(2), 10) };
}

/** Builds "4Q2025" from q and year */
export function buildQuarter(q: number, year: number): string {
  return `${q}Q${year}`;
}

/** Returns the current quarter string, e.g. "1Q2026" */
export function currentQuarter(): string {
  const now = new Date();
  return buildQuarter(Math.ceil((now.getMonth() + 1) / 3), now.getFullYear());
}

// ── Component ─────────────────────────────────────────────────────────────────

interface QuarterPickerProps {
  value: string;
  onChange: (quarter: string) => void;
  minYear?: number;
  maxYear?: number;
}

const QuarterPicker: React.FC<QuarterPickerProps> = ({
  value,
  onChange,
  minYear = 2017,
  maxYear = new Date().getFullYear(),
}) => {
  const { q, year } = parseQuarter(value);

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentQ = Math.ceil((now.getMonth() + 1) / 3);

  const isQuarterDisabled = (n: number) => year === currentYear && n > currentQ;

  const handleChange = (newQ: number, newYear: number) => {
    onChange(buildQuarter(newQ, newYear));
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
      {/* Year stepper */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <IconButton
          size="small"
          onClick={() => handleChange(q, year - 1)}
          disabled={year <= minYear}
        >
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
        <Typography variant="body1" sx={{ fontWeight: 600, minWidth: 44, textAlign: "center" }}>
          {year}
        </Typography>
        <IconButton
          size="small"
          onClick={() => handleChange(q, year + 1)}
          disabled={year >= maxYear}
        >
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Quarter toggle */}
      <ToggleButtonGroup
        value={q}
        exclusive
        size="small"
        onChange={(_, val) => { if (val !== null) handleChange(val, year); }}
      >
        {[1, 2, 3, 4].map((n) => (
          <ToggleButton
            key={n}
            value={n}
            disabled={isQuarterDisabled(n)}
            sx={{ px: 2, fontWeight: 600 }}
          >
            Q{n}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <Typography variant="body2" color="text.secondary">
        Selected: <strong>{value}</strong>
      </Typography>
    </Box>
  );
};

export default QuarterPicker;
